import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FlexBox, useBreakpoint } from '@brg/ui';
import {
    CheckHomeworkWrapper,
    CheckHomeworkHead,
    CheckHomeworkСhoiceWork,
    CheckHomeworkTheme,
    CheckHomeworkContent,
    CheckHomeworkSidebar,
    CheckHomeworkMain,
    CheckHomeworkStudentsList,
    CheckHomeworkStudent,
    CheckHomeworkMarkerViewed,
    CheckHomeworkTitle,
    CheckHomeworkAuthor,
    CheckHomeworkNextButton,
    CheckHomeworkCurrentWork,
    CheckHomeworkReview,
    CheckHomeworkAccept,
    CheckHomeworkSaveButton
} from './styles';
import { Input } from "@brg/input";
import { ArrowRightIcon } from 'shared/icons';
import {API, Endpoints, TLocal} from "@brg/api";
import {useTranslation} from "react-i18next";
import {IHomeWork, IThemeItem} from "../../constants/ILMS";
import {useGetHomeWork} from "../../hooks/api/LMS/useGetHomeWork";
import ResizedImageEnhanced from "../../components/ResizedImageEnhanced/ResizedImageEnhanced";
import { CheckBoxWrapper, RadioBox, RadioCircle, RadioInputSource, RadioLabel } from "shared/components/Radio/styles";
import { LayoutConst } from "core";
import {CommentHomework} from "./commentHomework";


export interface CheckHomeworkModalProps {
    currentTeamId?: string;
    projectId?: string;
    themeId?: string;
    title?: string;
    themes: IThemeItem<'Local'>[];
}

export const CheckHomeworkModal: FC<CheckHomeworkModalProps> = (props) => {

    const {
        projectId,
        themeId,
        title,
        themes
    } = props

    const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
    const { i18n, t } = useTranslation();
    const is1280 = useBreakpoint(LayoutConst.screen.l_12);

    const translatedThemes = useMemo(() => API.toLang(themes, i18n.language) as IThemeItem<TLocal>[], [themes, i18n.language])

    const [refreshTime, setRefreshTime] = useState(Date.now())
    const [activeTheme, setActiveTheme] = useState<null | {value: string, label: string}>(null)
    const themeOptions = useMemo(() => translatedThemes?.filter((i) => i?.needHomeWork)
        ?.map((i) => ({value: i?.id || "", label: i?.name || ""})) || [], [translatedThemes])

    useEffect(() => {
        setActiveTheme(themeOptions?.find(i => i?.value === themeId) || null)
    }, [themeOptions])

    const [activeHomework, setActiveHomework] = useState<null | IHomeWork>(null)
    const [activeHomeworkIsApprove, setActiveHomeworkIsApprove] = useState<boolean>(false)
    
    const [tempApproval, setTempApproval] = useState<boolean | undefined>(undefined);
    const [isSaving, setIsSaving] = useState(false);

    // @ts-ignore
    const homeWorkAttachments = useMemo(() => activeHomework?.homeWork?.post?.attachments?.length > 0 ? activeHomework?.homeWork?.post?.attachments : [], [activeHomework])

    const groupFilter = useMemo(() => ({
        projectId: projectId || "",
        themeId: activeTheme?.value || ""
    }), [activeTheme, projectId])
    const {homeWork} = useGetHomeWork(groupFilter, i18n.language, refreshTime);

    useEffect(() => {

        const homeWorkIndex = homeWork?.findIndex(i => i?.student?.accountId === activeHomework?.student?.accountId)
        if (homeWorkIndex !== -1) {
            setActiveHomework(homeWork[homeWorkIndex])
            setActiveHomeworkIsApprove(Boolean(homeWork[homeWorkIndex]?.homeWork?.approve?.isApprove))
            return;
        }

        setActiveHomework(homeWork?.length > 0 ? homeWork[0] : null)
        setActiveHomeworkIsApprove(homeWork?.length > 0 ? Boolean(homeWork[0]?.homeWork?.approve?.isApprove) : false)
    }, [homeWork, activeHomework])

    useEffect(() => {
        if (activeHomework) {
            setTempApproval(activeHomeworkIsApprove);
        }
    }, [activeHomework]);

    const hasChanges = useMemo(() => {
        return tempApproval !== undefined && tempApproval !== activeHomeworkIsApprove;
    }, [tempApproval, activeHomeworkIsApprove]);

    const hasValidationErrors = useMemo(() => {
        return tempApproval === undefined;
    }, [tempApproval]);

    const visibleNextButton = useMemo(() => {
        const homeWorkIndex = homeWork?.findIndex(i => i?.student?.accountId === activeHomework?.student?.accountId)
        return (homeWorkIndex + 1) < homeWork?.length;
    }, [homeWork, activeHomework])

    const onNextWorkClick = useCallback(() => {
        const homeWorkIndex = homeWork?.findIndex(i => i?.student?.accountId === activeHomework?.student?.accountId)
        if ((homeWorkIndex + 1) >= homeWork?.length) return;
        setActiveHomework(homeWork[homeWorkIndex + 1])
    }, [homeWork, activeHomework])

    const Radio = (props: any) => {
        return (
          <CheckBoxWrapper disabled={props.disabled}>
            <FlexBox>
              <RadioInputSource {...props as any} type={'radio'}/>
              <RadioBox {...props as any}>
                <RadioCircle {...props as any} />
                <RadioLabel {...props as any} className="accept-label" >
                  {props.title}
                </RadioLabel>
              </RadioBox>
            </FlexBox>
          </CheckBoxWrapper>
        )
      }

    const onTempApprovalSelect = useCallback((isApprove: boolean) => {
        setTempApproval(isApprove);
    }, []);

    // Функция сохранения (вызывается только при нажатии кнопки "Сохранить")
    const onSave = useCallback(async () => {
        if (!hasChanges || isSaving || tempApproval === undefined) return;

        setIsSaving(true);

        try {
            setActiveHomeworkIsApprove(tempApproval);

            await API.request()
                .endpoint(Endpoints.geoGateway)
                .url(`/api/LMS/ApproveHomeWork`)
                .post()
                .body({
                    moduleProjectId: activeHomework?.homeWork?.moduleProjectId,
                    themeLongreadId: activeHomework?.homeWork?.themeLongreadId,
                    postId: activeHomework?.homeWork?.postId,
                    studentId: activeHomework?.homeWork?.studentId,
                    isApprove: tempApproval,
                    comment: ""
                })
                .release()
                .then((r) => r.json);

            setRefreshTime(Date.now());
        } catch (error) {
            console.error('Ошибка при сохранении оценки:', error);
        } finally {
            setIsSaving(false);
        }
    }, [hasChanges, isSaving, tempApproval, activeHomework, setActiveHomeworkIsApprove, setRefreshTime])

    return (
        <FlexBox width={'100%'} justify='center' align='center' justify-content='center'>
            <CheckHomeworkWrapper>
                {is1280 ? (
                    <FlexBox direction="column" directionMobile="column" width={'100%'} widthMobile={'100%'}>
                        <CheckHomeworkHead>
                            <CheckHomeworkTheme>{title && title}</CheckHomeworkTheme>
                            <CheckHomeworkСhoiceWork>
                                <Input.Dropdown
                                    value={activeTheme}
                                    onChange={setActiveTheme}
                                    options={themeOptions}
                                />
                            </CheckHomeworkСhoiceWork>
                        </CheckHomeworkHead>
                        <CheckHomeworkContent>
                        {isMobile && (
                            <CheckHomeworkSidebar>
                                <CheckHomeworkStudentsList>
                                    {homeWork?.map((homework) =>
                                        <CheckHomeworkStudent isActive={activeHomework?.homeWork?.id === homework?.homeWork?.id} onClick={() => setActiveHomework(homework)} viewed={true} status={homework?.homeWork?.approve?.isApprove === true ? 'fine' : homework?.homeWork?.approve?.isApprove === false ? 'badly' : ''}>
                                            <CheckHomeworkMarkerViewed viewed={true}></CheckHomeworkMarkerViewed>{`${homework?.student?.name} ${homework?.student?.surName}`}
                                        </CheckHomeworkStudent>)}
                                </CheckHomeworkStudentsList>
                            </CheckHomeworkSidebar>
                        )}
                    {
                        <CheckHomeworkMain>
                            <CheckHomeworkTitle>
                                <CheckHomeworkAuthor>{`${activeHomework?.student?.name || ""} ${activeHomework?.student?.surName || ""}`}</CheckHomeworkAuthor>
                            </CheckHomeworkTitle>
                            {activeHomework?.homeWork ? <CheckHomeworkCurrentWork>
                                {<ResizedImageEnhanced files={homeWorkAttachments?.length > 0 ? (homeWorkAttachments[0]?.files || []) : []}/>}
                            </CheckHomeworkCurrentWork> : null}
                         </CheckHomeworkMain>
                    }
                    <FlexBox direction="row" justify="space-between" gx={16} gxMobile={isMobile ? 0 : 16}>
                        {!isMobile && (
                            <CheckHomeworkSidebar>
                                <CheckHomeworkStudentsList>
                                    {homeWork?.map((homework) =>
                                        <CheckHomeworkStudent isActive={activeHomework?.homeWork?.id === homework?.homeWork?.id} onClick={() => setActiveHomework(homework)} viewed={true} status={homework?.homeWork?.approve?.isApprove === true ? 'fine' : homework?.homeWork?.approve?.isApprove === false ? 'badly' : ''}>
                                            <CheckHomeworkMarkerViewed viewed={true}></CheckHomeworkMarkerViewed>{`${homework?.student?.name} ${homework?.student?.surName}`}
                                        </CheckHomeworkStudent>)}
                                </CheckHomeworkStudentsList>
                            </CheckHomeworkSidebar>
                        )}

                        <CheckHomeworkReview gy={16} gyMobile={16}>
                            <CheckHomeworkAccept>
                                <FlexBox justify="space-between" align="center">
                                    <FlexBox direction="column" gx={16}>
                                        <div className="accept-work">{t('lms.check_homework.apply_work')}</div>
                                        {isMobile ? (
                                            <FlexBox width={'100%'} widthMobile={'135px'}>
                                                <Radio title={t('yes')} checked={tempApproval === true} onClick={() => onTempApprovalSelect(true)}  />
                                                <Radio title={t('no')} checked={tempApproval === false} onClick={() => onTempApprovalSelect(false)}  />
                                            </FlexBox>
                                        ) : (
                                            <>
                                                <Radio title={t('yes')} checked={tempApproval === true} onClick={() => onTempApprovalSelect(true)}  />
                                                <Radio title={t('no')} checked={tempApproval === false} onClick={() => onTempApprovalSelect(false)}  />
                                            </>
                                        )}
                                    </FlexBox>
                                    <CheckHomeworkSaveButton 
                                        disabled={!hasChanges || isSaving || hasValidationErrors}
                                        onClick={onSave}
                                    >
                                        {t('lms.check_homework.save')}
                                    </CheckHomeworkSaveButton>
                                </FlexBox>
                            </CheckHomeworkAccept>
                            <CommentHomework postId={activeHomework?.homeWork?.postId || ""}/>

                            <FlexBox direction="row" directionMobile={isMobile ? 'column-reverse' : 'row'} justify={!visibleNextButton ? 'flex-end' : "space-between"} justifyMobile={!visibleNextButton ? 'flex-end' : "space-between"}>
                            {visibleNextButton && <CheckHomeworkNextButton onClick={onNextWorkClick}>{t('lms.check_homework.next_work')}
                                <ArrowRightIcon />
                                </CheckHomeworkNextButton>}
                            </FlexBox>
                        </CheckHomeworkReview>
                            
                    </FlexBox>
                        </CheckHomeworkContent>
                    </FlexBox>
                ) : (
                    <>
                <CheckHomeworkHead>
                    <CheckHomeworkTheme>{title && title}</CheckHomeworkTheme>
                    <CheckHomeworkСhoiceWork>
                        <Input.Dropdown
                            value={activeTheme}
                            onChange={setActiveTheme}
                            options={themeOptions}
                        />
                    </CheckHomeworkСhoiceWork>
                </CheckHomeworkHead>
                <CheckHomeworkContent>
                    <CheckHomeworkSidebar>
                        <CheckHomeworkStudentsList>
                            {homeWork?.map((homework) =>
                                <CheckHomeworkStudent isActive={activeHomework?.homeWork?.id === homework?.homeWork?.id} onClick={() => setActiveHomework(homework)} viewed={true} status={homework?.homeWork?.approve?.isApprove === true ? 'fine' : homework?.homeWork?.approve?.isApprove === false ? 'badly' : ''}>
                                    <CheckHomeworkMarkerViewed viewed={true}></CheckHomeworkMarkerViewed>{`${homework?.student?.name} ${homework?.student?.surName}`}
                                </CheckHomeworkStudent>)}
                        </CheckHomeworkStudentsList>
                    </CheckHomeworkSidebar>
                    {<CheckHomeworkMain>
                        <CheckHomeworkTitle>
                            <CheckHomeworkAuthor>{`${activeHomework?.student?.name || ""} ${activeHomework?.student?.surName || ""}`}</CheckHomeworkAuthor>
                            {visibleNextButton && <CheckHomeworkNextButton onClick={onNextWorkClick}>{t('lms.check_homework.next_work')}
                                <ArrowRightIcon />
                            </CheckHomeworkNextButton>}
                        </CheckHomeworkTitle>
                        {activeHomework?.homeWork ? <><CheckHomeworkCurrentWork>
                            {<ResizedImageEnhanced files={homeWorkAttachments?.length > 0 ? (homeWorkAttachments[0]?.files || []) : []}/>}
                        </CheckHomeworkCurrentWork>
                        <CheckHomeworkReview>
                            <CheckHomeworkAccept>
                                <FlexBox justify="space-between" align="center">
                                    <FlexBox direction="column" gx={16}>
                                        <div className="accept-work">{t('lms.check_homework.apply_work')}</div>
                                        <FlexBox>
                                            <Radio title={t('yes')} checked={tempApproval === true} onClick={() => onTempApprovalSelect(true)}  />
                                            <Radio title={t('no')} checked={tempApproval === false} onClick={() => onTempApprovalSelect(false)}  />
                                        </FlexBox>
                                    </FlexBox>
                                    <CheckHomeworkSaveButton 
                                        disabled={!hasChanges || isSaving || hasValidationErrors}
                                        onClick={onSave}
                                    >
                                        {t('lms.check_homework.save')}
                                    </CheckHomeworkSaveButton>
                                </FlexBox>
                            </CheckHomeworkAccept>
                            <CommentHomework postId={activeHomework?.homeWork?.postId || ""}/>
                        </CheckHomeworkReview>
                        </> : t('lms.check_homework.no_homework')}
                    </CheckHomeworkMain>}
                </CheckHomeworkContent>
                    </>
                )}
            </CheckHomeworkWrapper>
        </FlexBox>
    );
};
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FlexBox, useBreakpoint } from '@brg/ui';
import {
    CheckHomeworkWrapper,
    CheckHomeworkHead,
    CheckHomeworkСhoiceWork,
    CheckHomeworkTheme,
    CheckHomeworkContent,
    CheckHomeworkSidebar,
    CheckHomeworkMain,
    CheckHomeworkStudentsList,
    CheckHomeworkStudent,
    CheckHomeworkMarkerViewed,
    CheckHomeworkTitle,
    CheckHomeworkAuthor,
    CheckHomeworkNextButton,
    CheckHomeworkCurrentWork,
    CheckHomeworkReview,
    CheckHomeworkAccept,
    CheckHomeworkSaveButton
} from './styles';
import { Input } from "@brg/input";
import { ArrowRightIcon } from 'shared/icons';
import {API, Endpoints, TLocal} from "@brg/api";
import {useTranslation} from "react-i18next";
import {IHomeWork, IThemeItem} from "../../constants/ILMS";
import {useGetHomeWork} from "../../hooks/api/LMS/useGetHomeWork";
import ResizedImageEnhanced from "../../components/ResizedImageEnhanced/ResizedImageEnhanced";
import { CheckBoxWrapper, RadioBox, RadioCircle, RadioInputSource, RadioLabel } from "shared/components/Radio/styles";
import { LayoutConst } from "core";
import {CommentHomework} from "./commentHomework";


export interface CheckHomeworkModalProps {
    currentTeamId?: string;
    projectId?: string;
    themeId?: string;
    title?: string;
    themes: IThemeItem<'Local'>[];
}

export const CheckHomeworkModal: FC<CheckHomeworkModalProps> = (props) => {

    const {
        projectId,
        themeId,
        title,
        themes
    } = props

    const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
    const { i18n, t } = useTranslation();
    const is1280 = useBreakpoint(LayoutConst.screen.l_12);

    const translatedThemes = useMemo(() => API.toLang(themes, i18n.language) as IThemeItem<TLocal>[], [themes, i18n.language])

    const [refreshTime, setRefreshTime] = useState(Date.now())
    const [activeTheme, setActiveTheme] = useState<null | {value: string, label: string}>(null)
    const themeOptions = useMemo(() => translatedThemes?.filter((i) => i?.needHomeWork)
        ?.map((i) => ({value: i?.id || "", label: i?.name || ""})) || [], [translatedThemes])

    useEffect(() => {
        setActiveTheme(themeOptions?.find(i => i?.value === themeId) || null)
    }, [themeOptions])

    const [activeHomework, setActiveHomework] = useState<null | IHomeWork>(null)
    const [activeHomeworkIsApprove, setActiveHomeworkIsApprove] = useState<boolean>(false)
    
    const [tempApproval, setTempApproval] = useState<boolean | undefined>(undefined);
    const [isSaving, setIsSaving] = useState(false);

    // @ts-ignore
    const homeWorkAttachments = useMemo(() => activeHomework?.homeWork?.post?.attachments?.length > 0 ? activeHomework?.homeWork?.post?.attachments : [], [activeHomework])

    const groupFilter = useMemo(() => ({
        projectId: projectId || "",
        themeId: activeTheme?.value || ""
    }), [activeTheme, projectId])
    const {homeWork} = useGetHomeWork(groupFilter, i18n.language, refreshTime);

    useEffect(() => {

        const homeWorkIndex = homeWork?.findIndex(i => i?.student?.accountId === activeHomework?.student?.accountId)
        if (homeWorkIndex !== -1) {
            setActiveHomework(homeWork[homeWorkIndex])
            setActiveHomeworkIsApprove(Boolean(homeWork[homeWorkIndex]?.homeWork?.approve?.isApprove))
            return;
        }

        setActiveHomework(homeWork?.length > 0 ? homeWork[0] : null)
        setActiveHomeworkIsApprove(homeWork?.length > 0 ? Boolean(homeWork[0]?.homeWork?.approve?.isApprove) : false)
    }, [homeWork, activeHomework])

    useEffect(() => {
        if (activeHomework) {
            setTempApproval(activeHomeworkIsApprove);
        }
    }, [activeHomework]);

    const hasChanges = useMemo(() => {
        return tempApproval !== undefined && tempApproval !== activeHomeworkIsApprove;
    }, [tempApproval, activeHomeworkIsApprove]);

    const hasValidationErrors = useMemo(() => {
        return tempApproval === undefined;
    }, [tempApproval]);

    const visibleNextButton = useMemo(() => {
        const homeWorkIndex = homeWork?.findIndex(i => i?.student?.accountId === activeHomework?.student?.accountId)
        return (homeWorkIndex + 1) < homeWork?.length;
    }, [homeWork, activeHomework])

    const onNextWorkClick = useCallback(() => {
        const homeWorkIndex = homeWork?.findIndex(i => i?.student?.accountId === activeHomework?.student?.accountId)
        if ((homeWorkIndex + 1) >= homeWork?.length) return;
        setActiveHomework(homeWork[homeWorkIndex + 1])
    }, [homeWork, activeHomework])

    const Radio = (props: any) => {
        return (
          <CheckBoxWrapper disabled={props.disabled}>
            <FlexBox>
              <RadioInputSource {...props as any} type={'radio'}/>
              <RadioBox {...props as any}>
                <RadioCircle {...props as any} />
                <RadioLabel {...props as any} className="accept-label" >
                  {props.title}
                </RadioLabel>
              </RadioBox>
            </FlexBox>
          </CheckBoxWrapper>
        )
      }

    const onTempApprovalSelect = useCallback((isApprove: boolean) => {
        setTempApproval(isApprove);
    }, []);

    // Функция сохранения (вызывается только при нажатии кнопки "Сохранить")
    const onSave = useCallback(async () => {
        if (!hasChanges || isSaving || tempApproval === undefined) return;

        setIsSaving(true);

        try {
            setActiveHomeworkIsApprove(tempApproval);

            await API.request()
                .endpoint(Endpoints.geoGateway)
                .url(`/api/LMS/ApproveHomeWork`)
                .post()
                .body({
                    moduleProjectId: activeHomework?.homeWork?.moduleProjectId,
                    themeLongreadId: activeHomework?.homeWork?.themeLongreadId,
                    postId: activeHomework?.homeWork?.postId,
                    studentId: activeHomework?.homeWork?.studentId,
                    isApprove: tempApproval,
                    comment: ""
                })
                .release()
                .then((r) => r.json);

            setRefreshTime(Date.now());
        } catch (error) {
            console.error('Ошибка при сохранении оценки:', error);
        } finally {
            setIsSaving(false);
        }
    }, [hasChanges, isSaving, tempApproval, activeHomework, setActiveHomeworkIsApprove, setRefreshTime])

    return (
        <FlexBox width={'100%'} justify='center' align='center' justify-content='center'>
            <CheckHomeworkWrapper>
                {is1280 ? (
                    <FlexBox direction="column" directionMobile="column" width={'100%'} widthMobile={'100%'}>
                        <CheckHomeworkHead>
                            <CheckHomeworkTheme>{title && title}</CheckHomeworkTheme>
                            <CheckHomeworkСhoiceWork>
                                <Input.Dropdown
                                    value={activeTheme}
                                    onChange={setActiveTheme}
                                    options={themeOptions}
                                />
                            </CheckHomeworkСhoiceWork>
                        </CheckHomeworkHead>
                        <CheckHomeworkContent>
                        {isMobile && (
                            <CheckHomeworkSidebar>
                                <CheckHomeworkStudentsList>
                                    {homeWork?.map((homework) =>
                                        <CheckHomeworkStudent isActive={activeHomework?.homeWork?.id === homework?.homeWork?.id} onClick={() => setActiveHomework(homework)} viewed={true} status={homework?.homeWork?.approve?.isApprove === true ? 'fine' : homework?.homeWork?.approve?.isApprove === false ? 'badly' : ''}>
                                            <CheckHomeworkMarkerViewed viewed={true}></CheckHomeworkMarkerViewed>{`${homework?.student?.name} ${homework?.student?.surName}`}
                                        </CheckHomeworkStudent>)}
                                </CheckHomeworkStudentsList>
                            </CheckHomeworkSidebar>
                        )}
                    {
                        <CheckHomeworkMain>
                            <CheckHomeworkTitle>
                                <CheckHomeworkAuthor>{`${activeHomework?.student?.name || ""} ${activeHomework?.student?.surName || ""}`}</CheckHomeworkAuthor>
                            </CheckHomeworkTitle>
                            {activeHomework?.homeWork ? <CheckHomeworkCurrentWork>
                                {<ResizedImageEnhanced files={homeWorkAttachments?.length > 0 ? (homeWorkAttachments[0]?.files || []) : []}/>}
                            </CheckHomeworkCurrentWork> : null}
                         </CheckHomeworkMain>
                    }
                    <FlexBox direction="row" justify="space-between" gx={16} gxMobile={isMobile ? 0 : 16}>
                        {!isMobile && (
                            <CheckHomeworkSidebar>
                                <CheckHomeworkStudentsList>
                                    {homeWork?.map((homework) =>
                                        <CheckHomeworkStudent isActive={activeHomework?.homeWork?.id === homework?.homeWork?.id} onClick={() => setActiveHomework(homework)} viewed={true} status={homework?.homeWork?.approve?.isApprove === true ? 'fine' : homework?.homeWork?.approve?.isApprove === false ? 'badly' : ''}>
                                            <CheckHomeworkMarkerViewed viewed={true}></CheckHomeworkMarkerViewed>{`${homework?.student?.name} ${homework?.student?.surName}`}
                                        </CheckHomeworkStudent>)}
                                </CheckHomeworkStudentsList>
                            </CheckHomeworkSidebar>
                        )}

                        <CheckHomeworkReview gy={16} gyMobile={16}>
                            <CheckHomeworkAccept>
                                <FlexBox justify="space-between" align="center">
                                    <FlexBox direction="column" gx={16}>
                                        <div className="accept-work">{t('lms.check_homework.apply_work')}</div>
                                        {isMobile ? (
                                            <FlexBox width={'100%'} widthMobile={'135px'}>
                                                <Radio title={t('yes')} checked={tempApproval === true} onClick={() => onTempApprovalSelect(true)}  />
                                                <Radio title={t('no')} checked={tempApproval === false} onClick={() => onTempApprovalSelect(false)}  />
                                            </FlexBox>
                                        ) : (
                                            <>
                                                <Radio title={t('yes')} checked={tempApproval === true} onClick={() => onTempApprovalSelect(true)}  />
                                                <Radio title={t('no')} checked={tempApproval === false} onClick={() => onTempApprovalSelect(false)}  />
                                            </>
                                        )}
                                    </FlexBox>
                                    <CheckHomeworkSaveButton 
                                        disabled={!hasChanges || isSaving || hasValidationErrors}
                                        onClick={onSave}
                                    >
                                        {t('lms.check_homework.save')}
                                    </CheckHomeworkSaveButton>
                                </FlexBox>
                            </CheckHomeworkAccept>
                            <CommentHomework postId={activeHomework?.homeWork?.postId || ""}/>

                            <FlexBox direction="row" directionMobile={isMobile ? 'column-reverse' : 'row'} justify={!visibleNextButton ? 'flex-end' : "space-between"} justifyMobile={!visibleNextButton ? 'flex-end' : "space-between"}>
                            {visibleNextButton && <CheckHomeworkNextButton onClick={onNextWorkClick}>{t('lms.check_homework.next_work')}
                                <ArrowRightIcon />
                                </CheckHomeworkNextButton>}
                            </FlexBox>
                        </CheckHomeworkReview>
                            
                    </FlexBox>
                        </CheckHomeworkContent>
                    </FlexBox>
                ) : (
                    <>
                <CheckHomeworkHead>
                    <CheckHomeworkTheme>{title && title}</CheckHomeworkTheme>
                    <CheckHomeworkСhoiceWork>
                        <Input.Dropdown
                            value={activeTheme}
                            onChange={setActiveTheme}
                            options={themeOptions}
                        />
                    </CheckHomeworkСhoiceWork>
                </CheckHomeworkHead>
                <CheckHomeworkContent>
                    <CheckHomeworkSidebar>
                        <CheckHomeworkStudentsList>
                            {homeWork?.map((homework) =>
                                <CheckHomeworkStudent isActive={activeHomework?.homeWork?.id === homework?.homeWork?.id} onClick={() => setActiveHomework(homework)} viewed={true} status={homework?.homeWork?.approve?.isApprove === true ? 'fine' : homework?.homeWork?.approve?.isApprove === false ? 'badly' : ''}>
                                    <CheckHomeworkMarkerViewed viewed={true}></CheckHomeworkMarkerViewed>{`${homework?.student?.name} ${homework?.student?.surName}`}
                                </CheckHomeworkStudent>)}
                        </CheckHomeworkStudentsList>
                    </CheckHomeworkSidebar>
                    {<CheckHomeworkMain>
                        <CheckHomeworkTitle>
                            <CheckHomeworkAuthor>{`${activeHomework?.student?.name || ""} ${activeHomework?.student?.surName || ""}`}</CheckHomeworkAuthor>
                            {visibleNextButton && <CheckHomeworkNextButton onClick={onNextWorkClick}>{t('lms.check_homework.next_work')}
                                <ArrowRightIcon />
                            </CheckHomeworkNextButton>}
                        </CheckHomeworkTitle>
                        {activeHomework?.homeWork ? <><CheckHomeworkCurrentWork>
                            {<ResizedImageEnhanced files={homeWorkAttachments?.length > 0 ? (homeWorkAttachments[0]?.files || []) : []}/>}
                        </CheckHomeworkCurrentWork>
                        <CheckHomeworkReview>
                            <CheckHomeworkAccept>
                                <FlexBox justify="space-between" align="center">
                                    <FlexBox direction="column" gx={16}>
                                        <div className="accept-work">{t('lms.check_homework.apply_work')}</div>
                                        <FlexBox>
                                            <Radio title={t('yes')} checked={tempApproval === true} onClick={() => onTempApprovalSelect(true)}  />
                                            <Radio title={t('no')} checked={tempApproval === false} onClick={() => onTempApprovalSelect(false)}  />
                                        </FlexBox>
                                    </FlexBox>
                                    <CheckHomeworkSaveButton 
                                        disabled={!hasChanges || isSaving || hasValidationErrors}
                                        onClick={onSave}
                                    >
                                        {t('lms.check_homework.save')}
                                    </CheckHomeworkSaveButton>
                                </FlexBox>
                            </CheckHomeworkAccept>
                            <CommentHomework postId={activeHomework?.homeWork?.postId || ""}/>
                        </CheckHomeworkReview>
                        </> : t('lms.check_homework.no_homework')}
                    </CheckHomeworkMain>}
                </CheckHomeworkContent>
                    </>
                )}
            </CheckHomeworkWrapper>
        </FlexBox>
    );
};
